import { createSlice } from '@reduxjs/toolkit';
import { ACTION_TYPES } from '@views/AdminPanelView/components/AdminPanel/components/LinkInBio/components/MyPages/components/ActiveButtonModal/utils/constants.js';

export const ActiveButtonContextInitialsValues = {
  actionCTA: {},
  id: 0,
  elementGroup: '',
  elementType: '',
  elementId: 0,
  targetElementGroup: '',
  targetElementType: '',
  targetElementId: 0,
  actionTypeDefault: ACTION_TYPES.urlLink,
  actionType: '',
  actionValue: '',
  text: 'Website',
  backgroundColor: '#ffffff',
  textColor: '#000000',
  position: '',
  margin: 0,
  icon: 'none',
  border: 'none',
  borderColor: '#000000',
  shadow: 'soft',
  shadowColor: '#000000',
  shape: 'square',
  opacity: 0,
};

// After backend refactor: per-tab fields (title, content, image, label, cta) live inside
// blockContent.tabs[i]. Block-level fields here are shared styling + position + isTabsMode.
const makeEmptyTab = (n = 0) => ({
  id: 0,
  n,
  title: '',
  content: '',
  isContentTrim: false,
  contentTrimCount: 30,
  image: '',
  imageShape: '',
  label: '',
  labelColor: '',
  labelBackgroundColor: '',
  labelFontSize: null,
  labelAlign: 'left',
  labelShape: '',
  labelIsGradient: false,
  labelGradientColor: '',
  labelUrl: '',
  cta: [],
});

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  activeTabIndex: 0,

  blockContent: {
    // Shared typography
    titleAlign: 'left',
    titleLineHeight: 120,
    titleSize: 22,
    isHighlightTitle: false,
    titleBackgroundColor: '#000000',
    titleColor: '#ffffff',

    // Layout
    buttonLayout: 'vertical',
    titlePositionN: 1,
    contentPositionN: 2,
    imagePositionN: 3,
    buttonPositionN: 4,
    labelPositionN: 5,

    // Section / design bleed-through
    isSectionMinHeight: false,
    sectionMinHeight: 100,

    tabs: [makeEmptyTab()],

    // BlockHeading labels — separate from per-tab labels, still block-level.
    headingLabels: [],
  },
  containerDesign: {},
};

const normalizeTabs = (tabs) => {
  if (!Array.isArray(tabs) || tabs.length === 0) return [makeEmptyTab()];
  return tabs.map((t, i) => ({ ...makeEmptyTab(i), ...t, n: i }));
};

export const textSlice = createSlice({
  name: 'text',
  initialState,
  reducers: {
    textStore: (state, action) => {
      const payload = action.payload ?? {};
      const { id, isActive, sid, uid, data } = payload;

      const block = data ?? {};
      const blockContent = block.blockContent ?? {};
      const containerDesign = block.blockDesign?.containerDesign ?? {};

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;
      state.activeTabIndex = 0;

      state.containerDesign = containerDesign ?? {};
      state.blockContent = {
        isTabsMode: Boolean(blockContent.isTabsMode),

        titleAlign: blockContent.titleAlign ?? 'left',
        titleLineHeight: blockContent.titleLineHeight ?? 120,
        titleSize: blockContent.titleSize ?? 22,
        isHighlightTitle: blockContent.isHighlightTitle ?? false,
        titleBackgroundColor: blockContent.titleBackgroundColor ?? '#000000',
        titleColor: blockContent.titleColor ?? '#ffffff',

        buttonLayout: blockContent.buttonLayout ?? 'vertical',
        titlePositionN: blockContent.titlePositionN ?? 1,
        contentPositionN: blockContent.contentPositionN ?? 2,
        imagePositionN: blockContent.imagePositionN ?? 3,
        buttonPositionN: blockContent.buttonPositionN ?? 4,
        labelPositionN: blockContent.labelPositionN ?? 5,

        isSectionMinHeight: blockContent.isSectionMinHeight ?? false,
        sectionMinHeight: blockContent.sectionMinHeight ?? 100,

        tabs: normalizeTabs(blockContent.tabs),

        headingLabels: blockContent.headingLabels ?? [],
      };
    },
    textClear: () => initialState,

    textUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    textUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    textUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },

    textSetActiveTabIndex: (state, action) => {
      state.activeTabIndex = action.payload;
    },

    textUpdateTabs: (state, action) => {
      state.blockContent.tabs = normalizeTabs(action.payload);
    },

    // Patch a single tab's fields by index. Payload: { index, fields: {...} }
    textPatchTab: (state, action) => {
      const { index, fields } = action.payload ?? {};
      if (typeof index !== 'number') return;
      const tabs = state.blockContent.tabs;
      if (!tabs[index]) return;
      state.blockContent.tabs[index] = { ...tabs[index], ...fields };
    },

    // Replace the CTA list of a specific tab (by tabId). Payload: { tabId, cta }
    textSetTabCta: (state, action) => {
      const { tabId, cta } = action.payload ?? {};
      const idx = state.blockContent.tabs.findIndex((t) => t.id === tabId);
      if (idx === -1) return;
      state.blockContent.tabs[idx].cta = cta ?? [];
    },
  },
});

export const {
  textUpdateIsLoading,
  textStore,
  textClear,
  textUpdateIsActive,
  textUpdateBlockContent,
  textUpdateTabs,
  textSetActiveTabIndex,
  textPatchTab,
  textSetTabCta,
} = textSlice.actions;

export const selectTextIsLoading = (state) => state.text.isLoading;
export const selectTextIsActive = (state) => state.text.isActive;
export const selectTextId = (state) => state.text.id;
export const selectTextUID = (state) => state.text.uid;
export const selectTextSID = (state) => state.text.sid;
export const selectTextActiveTabIndex = (state) => state.text.activeTabIndex;

export const selectTextContainerDesign = (state) => state.text.containerDesign;
export const selectTextBlockContent = (state) => state.text.blockContent;
export const selectTextTabs = (state) => state.text.blockContent.tabs;
export const selectTextActiveTab = (state) => {
  const tabs = state.text.blockContent.tabs ?? [];
  const idx = Math.min(state.text.activeTabIndex, Math.max(tabs.length - 1, 0));
  return tabs[idx] ?? null;
};

export default textSlice.reducer;
